import React, { useState } from "react";
import { Plus, X, Loader2, Save, ToggleLeft, ToggleRight } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FeatureCategoryBadge } from "./FeatureBadges";

const CATEGORIES = ["core", "sales", "marketing", "reports", "integration", "users", "support", "other"];

function CreateFeatureDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ feature_key: "", feature_name: "", feature_description: "", feature_category: "other" });
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) setForm({ feature_key: "", feature_name: "", feature_description: "", feature_category: "other" }); }, [open]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/platform/features", form);
      toast.success(`Feature "${r.data.feature.feature_key}" ditambahkan.`);
      onCreated?.();
      onClose?.();
    } catch (e) { notifyError(e, "Gagal menambah feature"); } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm grid place-items-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()} data-testid="feature-create-dialog">
      <div className="w-full max-w-lg bg-[#0F0F17] border border-white/10 rounded-3xl">
        <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
          <div className="text-white font-display font-black text-base">Tambah Feature Baru</div>
          <button onClick={onClose} className="ml-auto w-8 h-8 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10" data-testid="feature-create-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-6 space-y-3">
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Feature Key *</label>
            <Input value={form.feature_key} onChange={(e) => setForm({ ...form, feature_key: e.target.value.toLowerCase() })}
              placeholder="sales.pos_offline" className="bg-white/5 border-white/10 text-white font-mono" data-testid="input-feature-key" />
            <div className="text-[11px] text-white/40 mt-1">Format lowercase dot-notation (min 1 dot).</div>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Feature Name *</label>
            <Input value={form.feature_name} onChange={(e) => setForm({ ...form, feature_name: e.target.value })}
              placeholder="POS Offline" className="bg-white/5 border-white/10 text-white" data-testid="input-feature-name" />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Category</label>
            <div className="flex flex-wrap gap-1.5">
              {CATEGORIES.map((c) => (
                <button key={c} type="button" onClick={() => setForm({ ...form, feature_category: c })}
                  className={`px-2.5 py-1 rounded-full border text-[10px] font-black uppercase tracking-wider ${
                    form.feature_category === c ? "bg-white text-black border-white" : "border-white/10 text-white/60 hover:text-white"
                  }`} data-testid={`category-option-${c}`}>{c}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-bold text-white/60 uppercase tracking-wider mb-1.5">Description</label>
            <Textarea value={form.feature_description} onChange={(e) => setForm({ ...form, feature_description: e.target.value })}
              rows={2} className="bg-white/5 border-white/10 text-white" data-testid="input-feature-description" />
          </div>
        </div>
        <div className="px-6 py-4 border-t border-white/10 flex items-center gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white hover:bg-white/10">Batal</Button>
          <Button onClick={submit} disabled={busy || !form.feature_key || !form.feature_name}
            className="bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40" data-testid="feature-create-submit">
            {busy ? <><Loader2 size={14} className="mr-1 animate-spin" /> Menyimpan…</> : <><Plus size={14} className="mr-1" /> Tambah</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function FeatureCatalogTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/platform/features");
      setItems(r.data.items || []);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const toggleActive = async (f) => {
    try {
      await api.put(`/platform/features/${f.feature_key}`, { is_active: !f.is_active });
      toast.success(`${f.feature_key} → ${!f.is_active ? "aktif" : "nonaktif"}`);
      load();
    } catch (e) { notifyError(e); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/platform/features/${editing.feature_key}`, {
        feature_name: editing.feature_name,
        feature_description: editing.feature_description,
        feature_category: editing.feature_category,
      });
      toast.success("Perubahan disimpan.");
      setEditing(null);
      load();
    } catch (e) { notifyError(e); } finally { setSaving(false); }
  };

  // Grouping by category
  const grouped = items.reduce((acc, f) => {
    const c = f.feature_category || "other";
    (acc[c] = acc[c] || []).push(f);
    return acc;
  }, {});

  return (
    <div data-testid="tab-catalog">
      <div className="flex items-center gap-3 mb-4">
        <div className="text-xs text-white/50">Total: {items.length} features</div>
        <Button onClick={() => setShowCreate(true)} className="ml-auto bg-white text-black hover:bg-white/90" data-testid="btn-add-feature">
          <Plus size={14} className="mr-1" /> Tambah Feature
        </Button>
      </div>

      {loading ? (
        <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([cat, feats]) => (
            <div key={cat} className="rounded-2xl border border-white/10 overflow-hidden">
              <div className="px-4 py-2 bg-white/5 flex items-center gap-2">
                <FeatureCategoryBadge category={cat} />
                <span className="text-xs text-white/40">{feats.length} feature</span>
              </div>
              {feats.map((f) => {
                const isEdit = editing?.feature_key === f.feature_key;
                return (
                  <div key={f.feature_key} className="px-4 py-3 border-t border-white/5 flex items-center gap-3" data-testid={`feature-row-${f.feature_key}`}>
                    <button onClick={() => toggleActive(f)} data-testid={`feature-toggle-${f.feature_key}`}
                      className={`shrink-0 ${f.is_active ? "text-emerald-400" : "text-white/30"}`}>
                      {f.is_active ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-white/70">{f.feature_key}</span>
                        {!f.is_active && <span className="text-[10px] text-rose-400">INACTIVE</span>}
                      </div>
                      {isEdit ? (
                        <div className="mt-2 space-y-2">
                          <Input value={editing.feature_name} onChange={(e) => setEditing({ ...editing, feature_name: e.target.value })}
                            className="bg-white/5 border-white/10 text-white text-sm" data-testid={`edit-name-${f.feature_key}`} />
                          <Textarea value={editing.feature_description || ""} onChange={(e) => setEditing({ ...editing, feature_description: e.target.value })}
                            rows={2} className="bg-white/5 border-white/10 text-white text-sm" />
                          <div className="flex flex-wrap gap-1.5">
                            {CATEGORIES.map((c) => (
                              <button key={c} type="button" onClick={() => setEditing({ ...editing, feature_category: c })}
                                className={`px-2 py-0.5 rounded-full border text-[10px] font-black uppercase ${
                                  editing.feature_category === c ? "bg-white text-black border-white" : "border-white/10 text-white/60"
                                }`}>{c}</button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="text-sm text-white/90 font-bold">{f.feature_name}</div>
                          {f.feature_description && <div className="text-[11px] text-white/50 mt-0.5">{f.feature_description}</div>}
                        </>
                      )}
                    </div>
                    {isEdit ? (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="text-white/60 h-8">Batal</Button>
                        <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-emerald-500 text-white hover:bg-emerald-600 h-8" data-testid={`btn-save-feature-${f.feature_key}`}>
                          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                        </Button>
                      </div>
                    ) : (
                      <Button size="sm" variant="ghost" onClick={() => setEditing({ ...f })} data-testid={`btn-edit-feature-${f.feature_key}`}
                        className="text-white/60 hover:text-white hover:bg-white/10 h-8">Edit</Button>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      <CreateFeatureDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
    </div>
  );
}
